import * as React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../utils/cn'
import { FieldLabelContent } from './field-label'

/**
 * JPG Academy Input
 *
 * Form inputs with warm focus rings and clear validation states.
 * Motion is built in and respects prefers-reduced-motion.
 */

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
	label?: string
	labelHelp?: React.ReactNode
	labelHelpTitle?: string
	hint?: string
	error?: string
	inputSize?: 'sm' | 'md' | 'lg'
	multiline?: boolean
	rows?: number
}

const inputSizeStyles = {
	sm: 'h-8 text-xs px-2.5',
	md: 'h-10 text-sm px-3',
	lg: 'h-12 text-base px-4',
}

export const Input = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
	({ className, label, labelHelp, labelHelpTitle, hint, error, inputSize = 'md', multiline = false, rows, id, ...props }, ref) => {
		const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')
		const inputClasses = cn(
			'w-full font-ui rounded-md border transition-all duration-fast ease-default',
			'bg-background text-foreground placeholder:text-muted-foreground',
			'disabled:cursor-not-allowed disabled:opacity-50',
			'focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold',
			inputSizeStyles[inputSize],
			error ? 'border-error/60 focus:ring-error/30 focus:border-error' : 'border-input hover:border-navy-300',
			className,
		)

		return (
			<motion.div className="space-y-1.5" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}>
				{label && <label htmlFor={inputId} className="block text-sm font-medium text-foreground font-ui"><FieldLabelContent label={label} labelHelp={labelHelp} labelHelpTitle={labelHelpTitle} /></label>}
				{multiline ? (
					<textarea
						ref={ref as React.Ref<HTMLTextAreaElement>}
						id={inputId}
						rows={rows}
						className={cn(inputClasses, 'min-h-[80px] resize-y')}
						aria-invalid={!!error}
						aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
						{...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
					/>
				) : (
					<input
						ref={ref as React.Ref<HTMLInputElement>}
						id={inputId}
						className={inputClasses}
						aria-invalid={!!error}
						aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
						{...props}
					/>
				)}
				{error && (
					<motion.p id={`${inputId}-error`} className="text-xs text-error font-ui" initial={{ opacity: 0, y: -4, height: 0 }} animate={{ opacity: 1, y: 0, height: 'auto' }} exit={{ opacity: 0, y: -4, height: 0 }} transition={{ duration: 0.2 }}>
						{error}
					</motion.p>
				)}
				{hint && !error && <p id={`${inputId}-hint`} className="text-xs text-muted-foreground font-ui">{hint}</p>}
			</motion.div>
		)
	},
)

Input.displayName = 'Input'
